import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { enquiryAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { defaultFormValues, LABELS } from '../constants/enquiryFormOptions';
import {
  clearFormDraft,
  hasMeaningfulDraft,
  loadFormDraft,
  saveFormDraft,
  shouldShowDraftRestoreNotice,
} from '../utils/formDraftStorage';
import { enquiryToFormValues } from '../utils/enquiryToFormValues';
import {
  getRules,
  getStepFieldNames,
  isStepComplete,
  showFieldError,
} from '../utils/stepValidation';
import {
  Field,
  Input,
  Select,
  Textarea,
  RadioGroup,
  CheckboxGroup,
} from '../components/FormControls';
import SkillRatingsSection from '../components/SkillRatingsSection';

const STEPS = ['Personal', 'Background', 'Course & Goals', 'Skills', 'Review'];

function toOptions(map) {
  return Object.entries(map);
}

function formatApiError(err) {
  if (!err.response) {
    return (
      err.message ||
      'Could not reach the server. Start the quiz backend (port 3040) and refresh, then try again.'
    );
  }

  const { status, data } = err.response;
  const errors = data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .map((e) => e.msg || e.message || `${e.path || e.param}: invalid value`)
      .join('. ');
  }

  if (data?.message) return data.message;

  if (status === 404) {
    return 'Enquiry API is not available on this server. Use local dev (backend on port 3040) or deploy the latest backend.';
  }

  return `Submission failed (${status}). Please check your details.`;
}

export default function EnquiryFormPage() {
  const draft = useMemo(() => {
    const loaded = loadFormDraft();
    if (!loaded || !hasMeaningfulDraft(loaded.formData, loaded.step)) {
      return null;
    }
    return loaded;
  }, []);
  const [step, setStep] = useState(draft?.step ?? 0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [draftRestored, setDraftRestored] = useState(() =>
    shouldShowDraftRestoreNotice(draft)
  );
  const [lookupLoading, setLookupLoading] = useState(false);
  const [stepValidated, setStepValidated] = useState(false);
  const lastLookupKey = useRef('');
  const navigate = useNavigate();
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    trigger,
    formState: { errors, touchedFields },
  } = useForm({
    defaultValues: draft?.formData ?? defaultFormValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const err = (name) => showFieldError(errors, touchedFields, name, stepValidated);
  const getSkillError = (index) =>
    showFieldError(errors, touchedFields, `skillRatings.${index}.level`, stepValidated);

  const formValues = watch();
  const totalExperience = watch('work.totalExperience');
  const highestQualification = watch('education.highestQualification');
  const currentStatus = watch('education.currentStatus');
  const hearAboutUs = watch('coursePreference.hearAboutUs');

  const validationContext = {
    highestQualification,
    totalExperience,
    hearAboutUs,
    currentStatus,
  };
  const canProceed = useMemo(
    () => isStepComplete(step, formValues, validationContext),
    [step, formValues, highestQualification, totalExperience, hearAboutUs, currentStatus]
  );

  useEffect(() => {
    setStepValidated(false);
  }, [step]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (hasMeaningfulDraft(formValues, step)) {
        saveFormDraft(formValues, step);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formValues, step]);

  useEffect(() => {
    if (highestQualification !== 'other') {
      setValue('education.highestQualificationOther', '');
    }
  }, [highestQualification, setValue]);

  useEffect(() => {
    trigger('education.yearOfPassing');
  }, [currentStatus, trigger]);

  useEffect(() => {
    if (totalExperience === 'fresher') {
      setValue('work.lastCompany', '');
      setValue('work.lastDesignation', '');
      setValue('work.knownSkills', '');
    }
  }, [totalExperience, setValue]);

  const lookupExistingEnquiry = async () => {
    const trimmedEmail = getValues('personal.email')?.trim().toLowerCase() ?? '';
    const trimmedMobile = getValues('personal.mobile')?.trim() ?? '';
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const mobileValid = /^[6-9]\d{9}$/.test(trimmedMobile);

    if (!emailValid && !mobileValid) return;

    const lookupKey = `${trimmedEmail}|${trimmedMobile}`;
    if (lookupKey === lastLookupKey.current) return;

    setLookupLoading(true);
    try {
      const params = {};
      if (emailValid) params.email = trimmedEmail;
      if (mobileValid) params.mobile = trimmedMobile;

      const res = await enquiryAPI.lookup(params);
      if (!res.data.found || !res.data.enquiry) return;

      lastLookupKey.current = lookupKey;
      const formData = enquiryToFormValues(res.data.enquiry);
      reset(formData);
      setDraftRestored(false);

      const name = res.data.enquiry.personal?.fullName?.trim();
      showToast(
        name
          ? `Welcome back, ${name}! We found your previous enquiry and pre-filled the form. Please review and update anything that has changed.`
          : 'We found a previous enquiry with this email or mobile and pre-filled the form. Please review and update anything that has changed.',
        'success'
      );
    } catch {
      // lookup failed silently — user can continue filling manually
    } finally {
      setLookupLoading(false);
    }
  };

  const lookingFor = watch('careerGoals.lookingFor') || [];
  const rules = (name) => getRules(name, validationContext);

  const next = async () => {
    const fields = getStepFieldNames(step, validationContext);
    setStepValidated(true);
    if (fields.length && !(await trigger(fields))) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onSubmit = async (data) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        personal: {
          ...data.personal,
          nationality: 'Indian',
          whatsapp: data.personal.whatsapp || undefined,
        },
        education: {
          ...data.education,
          yearOfPassing: Number(data.education.yearOfPassing),
          cgpaOrPercentage: Number(data.education.cgpaOrPercentage),
          highestQualificationOther:
            data.education.highestQualification === 'other'
              ? data.education.highestQualificationOther?.trim()
              : undefined,
        },
        work: {
          totalExperience: data.work.totalExperience,
          lastCompany: data.work.lastCompany || undefined,
          lastDesignation: data.work.lastDesignation || undefined,
          knownSkills: data.work.knownSkills
            ? data.work.knownSkills.split(',').map((s) => s.trim()).filter(Boolean)
            : [],
        },
        coursePreference: {
          ...data.coursePreference,
          hearAboutUsOther:
            data.coursePreference.hearAboutUs === 'other'
              ? data.coursePreference.hearAboutUsOther
              : undefined,
        },
        careerGoals: {
          ...data.careerGoals,
          expectedSalaryLpa: data.careerGoals.expectedSalaryLpa
            ? Number(data.careerGoals.expectedSalaryLpa)
            : undefined,
        },
        skillRatings: data.skillRatings.map((r) => ({
          skill: r.skill,
          level: r.level,
        })),
        declaration: { accepted: true },
      };

      const res = await enquiryAPI.create(payload);
      clearFormDraft();
      navigate('/enquiry/thank-you', {
        state: { enquiryNumber: res.data.enquiryNumber },
      });
    } catch (err) {
      setSubmitError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card form-card">
      {draftRestored && (
        <p className="draft-notice" role="status">
          Your previous answers were restored from this device.
          <button
            type="button"
            className="draft-notice-dismiss"
            onClick={() => setDraftRestored(false)}
          >
            Dismiss
          </button>
        </p>
      )}
      {lookupLoading && (
        <p className="lookup-hint" role="status">
          Checking for previous enquiry…
        </p>
      )}
      <div className="form-progress" aria-live="polite">
        <div className="form-progress-label">
          <span className="form-progress-step">
            Step {step + 1} of {STEPS.length}
          </span>
          <strong>{STEPS[step]}</strong>
        </div>
        <div className="form-progress-bar" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={STEPS.length}>
          <div
            className="form-progress-fill"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="stepper" aria-hidden="true">
        {STEPS.map((label, i) => (
          <span
            key={label}
            className={`stepper-item ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
          >
            <span className="stepper-num">{i + 1}</span>
            <span className="stepper-text">{label}</span>
          </span>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {step === 0 && (
          <section>
            <h2>1. Personal Information</h2>
            <div className="grid two-col">
              <Field label="Full name" required error={err('personal.fullName')}>
                <Input register={register} name="personal.fullName" registerOptions={rules('personal.fullName')} />
              </Field>
              <Field label="Date of birth" required error={err('personal.dateOfBirth')}>
                <Input register={register} name="personal.dateOfBirth" type="date" registerOptions={rules('personal.dateOfBirth')} />
              </Field>
              <Field label="Gender" required error={err('personal.gender')}>
                <RadioGroup
                  register={register}
                  name="personal.gender"
                  registerOptions={rules('personal.gender')}
                  options={toOptions(LABELS.gender)}
                />
              </Field>
              <Field label="Mobile number" required error={err('personal.mobile')}>
                <Input
                  register={register}
                  name="personal.mobile"
                  placeholder="10-digit"
                  registerOptions={rules('personal.mobile')}
                  onBlur={lookupExistingEnquiry}
                />
              </Field>
              <Field label="WhatsApp (if different)">
                <Input register={register} name="personal.whatsapp" />
              </Field>
              <Field label="Email" required error={err('personal.email')}>
                <Input
                  register={register}
                  name="personal.email"
                  type="email"
                  registerOptions={rules('personal.email')}
                  onBlur={lookupExistingEnquiry}
                />
              </Field>
              <Field label="City" required error={err('personal.address.city')}>
                <Input register={register} name="personal.address.city" registerOptions={rules('personal.address.city')} />
              </Field>
              <Field label="State" required error={err('personal.address.state')}>
                <Input register={register} name="personal.address.state" registerOptions={rules('personal.address.state')} />
              </Field>
            </div>
          </section>
        )}

        {step === 1 && (
          <section>
            <h2>2. Educational & Work Background</h2>
            <div className="grid two-col">
              <Field label="Highest qualification" required error={err('education.highestQualification')}>
                <Select
                  register={register}
                  name="education.highestQualification"
                  registerOptions={rules('education.highestQualification')}
                  options={toOptions(LABELS.qualification)}
                />
              </Field>
              {highestQualification === 'other' && (
                <Field
                  label="Please specify your qualification"
                  required
                  error={err('education.highestQualificationOther')}
                >
                  <Input
                    register={register}
                    name="education.highestQualificationOther"
                    registerOptions={rules('education.highestQualificationOther')}
                    placeholder="e.g. B.Com, Diploma in Design"
                  />
                </Field>
              )}
              <Field label="Current status" required error={err('education.currentStatus')}>
                <Select
                  register={register}
                  name="education.currentStatus"
                  registerOptions={rules('education.currentStatus')}
                  options={toOptions(LABELS.currentStatus)}
                />
              </Field>
              <Field label="College / University" required error={err('education.collegeName')}>
                <Input register={register} name="education.collegeName" registerOptions={rules('education.collegeName')} />
              </Field>
              <Field label="Branch / Specialization" required error={err('education.branch')}>
                <Input register={register} name="education.branch" registerOptions={rules('education.branch')} />
              </Field>
              <Field label="Year of passing" required error={err('education.yearOfPassing')}>
                <Input register={register} name="education.yearOfPassing" type="number" registerOptions={rules('education.yearOfPassing')} />
              </Field>
              <Field label="CGPA" required error={err('education.cgpaOrPercentage')}>
                <Input
                  register={register}
                  name="education.cgpaOrPercentage"
                  type="number"
                  step="0.01"
                  min="0"
                  max="10"
                  registerOptions={rules('education.cgpaOrPercentage')}
                />
              </Field>
              <Field label="Total experience" required error={err('work.totalExperience')}>
                <Select
                  register={register}
                  name="work.totalExperience"
                  registerOptions={rules('work.totalExperience')}
                  options={toOptions(LABELS.experience)}
                />
              </Field>
            </div>
            {totalExperience && totalExperience !== 'fresher' && (
              <>
                <h3 className="subsection-title">Work experience</h3>
                <div className="grid two-col">
                  <Field label="Current / Last company">
                    <Input register={register} name="work.lastCompany" />
                  </Field>
                  <Field label="Current / Last designation">
                    <Input register={register} name="work.lastDesignation" />
                  </Field>
                  <Field label="Key skills / technologies (comma-separated)" className="full-width">
                    <Input register={register} name="work.knownSkills" />
                  </Field>
                </div>
              </>
            )}
          </section>
        )}

        {step === 2 && (
          <section>
            <h2>3. Course Preference & Career Goals</h2>
            <div className="grid two-col">
              <Field label="Course interested in" required error={err('coursePreference.courseInterested')}>
                <Select
                  register={register}
                  name="coursePreference.courseInterested"
                  registerOptions={rules('coursePreference.courseInterested')}
                  options={toOptions(LABELS.course)}
                />
              </Field>
              <Field label="Preferred batch mode" required error={err('coursePreference.batchMode')}>
                <Select
                  register={register}
                  name="coursePreference.batchMode"
                  registerOptions={rules('coursePreference.batchMode')}
                  options={toOptions(LABELS.batchMode)}
                />
              </Field>
              <Field label="Preferred start month" required error={err('coursePreference.preferredStartMonth')}>
                <Input register={register} name="coursePreference.preferredStartMonth" placeholder="e.g. July 2026" registerOptions={rules('coursePreference.preferredStartMonth')} />
              </Field>
              <Field label="How did you hear about us?" required error={err('coursePreference.hearAboutUs')}>
                <Select
                  register={register}
                  name="coursePreference.hearAboutUs"
                  registerOptions={rules('coursePreference.hearAboutUs')}
                  options={toOptions(LABELS.hearAboutUs)}
                />
              </Field>
              {hearAboutUs === 'other' && (
                <Field label="Please specify">
                  <Input register={register} name="coursePreference.hearAboutUsOther" />
                </Field>
              )}
              <Field label="Primary goal after course" required error={err('coursePreference.primaryGoalAfterCourse')}>
                <Select
                  register={register}
                  name="coursePreference.primaryGoalAfterCourse"
                  registerOptions={rules('coursePreference.primaryGoalAfterCourse')}
                  options={toOptions(LABELS.primaryGoal)}
                />
              </Field>
            </div>
            <Field label="Why do you want to join this course?" required error={err('careerGoals.whyJoin')}>
              <Textarea register={register} name="careerGoals.whyJoin" registerOptions={rules('careerGoals.whyJoin')} />
            </Field>
            <Field label="Career goal (2–3 years)" required error={err('careerGoals.careerGoal2To3Years')}>
              <Textarea register={register} name="careerGoals.careerGoal2To3Years" registerOptions={rules('careerGoals.careerGoal2To3Years')} />
            </Field>
            <Field label="I am looking for">
              <CheckboxGroup
                value={lookingFor}
                onChange={(v) => setValue('careerGoals.lookingFor', v)}
                options={toOptions(LABELS.lookingFor)}
              />
            </Field>
            <div className="grid two-col">
              <Field label="Expected salary after course (₹ LPA)" error={err('careerGoals.expectedSalaryLpa')}>
                <Input
                  register={register}
                  name="careerGoals.expectedSalaryLpa"
                  type="number"
                  step="0.1"
                  min="0"
                  registerOptions={rules('careerGoals.expectedSalaryLpa')}
                />
              </Field>
              <Field label="Willing to relocate?" required error={err('careerGoals.willingToRelocate')}>
                <RadioGroup
                  register={register}
                  name="careerGoals.willingToRelocate"
                  registerOptions={rules('careerGoals.willingToRelocate')}
                  options={toOptions(LABELS.relocate)}
                />
              </Field>
            </div>
          </section>
        )}

        {step === 3 && (
          <section>
            <h2>4. Self-Rating — Technical Skills</h2>
            <p className="muted">Please rate every skill below. This helps our counselors guide you better.</p>
            <SkillRatingsSection
              register={register}
              setValue={setValue}
              watch={watch}
              getSkillError={getSkillError}
            />
          </section>
        )}

        {step === 4 && (
          <section>
            <h2>5. Review & Submit</h2>
            <p className="muted">
              Please confirm that all information is correct before submitting.
            </p>
            <label className="checkbox-item declaration">
              <input type="checkbox" {...register('declaration.accepted', rules('declaration.accepted'))} />
              <span>I confirm the information provided is accurate.</span>
            </label>
            {err('declaration.accepted') && (
              <span className="field-error">{err('declaration.accepted')}</span>
            )}
          </section>
        )}

        {submitError && <p className="alert alert-error">{submitError}</p>}

        <div className="form-actions">
          {step > 0 && (
            <button type="button" className="btn btn-secondary" onClick={back}>
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={next}
              disabled={!canProceed}
              title={!canProceed ? 'Fill all required fields to continue' : undefined}
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !canProceed}
              title={!canProceed ? 'Accept the declaration to submit' : undefined}
            >
              {submitting ? 'Submitting…' : 'Submit Enquiry'}
            </button>
          )}
        </div>
        {!canProceed && step < STEPS.length - 1 && (
          <p className="step-hint muted">Complete all required fields (*) to continue.</p>
        )}
      </form>
    </div>
  );
}
